import { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle2, Loader2, ChevronDown, ChevronRight, ArrowUp, AlertTriangle } from 'lucide-react';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import { OrbisMarkSVG } from '@/components/OrbisLogo';

function formatDuration(ms) {
  if (ms == null) return '';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function formatElapsed(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}:${String(s % 60).padStart(2, '0')}` : `${s}s`;
}

// Step dot indicator
function StepDot({ status }) {
  if (status === 'done') return (
    <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(143,196,160,0.15)', border: '1.5px solid var(--orbis-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--orbis-green)' }} />
    </div>
  );
  if (status === 'running') return (
    <div style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <OrbisSpinner size={14} color="var(--orbis-blue)" />
    </div>
  );
  if (status === 'error') return (
    <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '1.5px solid #E07070', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#E07070' }} />
    </div>
  );
  return <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.12)', flexShrink: 0 }} />;
}

// Collapsible tool card — shows action + step list
function ToolCard({ card, buildState }) {
  const [open, setOpen] = useState(true);

  // If this card has live build state wired in
  const steps = buildState?.steps || card.steps || [];
  const thinkingMessages = buildState?.thinkingMessages || [];
  const isComplete = buildState?.isComplete ?? card.isComplete;
  const elapsedMs = buildState?.elapsedMs;

  const runningStep = steps.find(s => s.status === 'running');
  const latestThinking = runningStep
    ? thinkingMessages.filter(t => t.stepId === runningStep.id).slice(-1)[0]?.message
    : null;

  const actionLabel = card.label || (isComplete ? 'Completed' : 'Working');
  const description = card.description || latestThinking || '';

  return (
    <div style={{ background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden', marginTop: '10px' }}>
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2.5"
        style={{ padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <div style={{ width: '20px', height: '20px', borderRadius: '6px', background: isComplete ? 'rgba(143,196,160,0.15)' : 'rgba(184,204,223,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {isComplete
            ? <CheckCircle2 className="w-3 h-3" style={{ color: 'var(--orbis-green)' }} />
            : <OrbisSpinner size={14} color="var(--orbis-blue)" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span style={{ fontSize: '13px', fontWeight: 400, color: 'var(--orbis-warm)' }}>{actionLabel}</span>
            {elapsedMs && isComplete && (
              <span style={{ fontSize: '11px', color: '#3A3835' }}>{formatElapsed(elapsedMs)}</span>
            )}
          </div>
          {description && (
            <p style={{ fontSize: '11px', color: '#5A5855', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {description}
            </p>
          )}
        </div>
        {open
          ? <ChevronDown className="w-3 h-3 shrink-0" style={{ color: '#5A5855' }} />
          : <ChevronRight className="w-3 h-3 shrink-0" style={{ color: '#5A5855' }} />
        }
      </button>

      {/* Step list */}
      {open && steps.length > 0 && (
        <div style={{ padding: '2px 12px 12px', display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '0.5px solid rgba(255,255,255,0.05)' }}>
          {steps.map(step => (
            <div key={step.id} className="flex flex-col" style={{ paddingTop: '6px' }}>
              <div className="flex items-center gap-2.5">
                <StepDot status={step.status} />
                <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                  <span style={{
                    fontSize: '12px', fontWeight: 300,
                    color: step.status === 'done' ? '#5A5855' : step.status === 'running' ? 'var(--orbis-warm)' : 'rgba(255,255,255,0.2)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {step.label}
                  </span>
                  {step.status === 'done' && step.duration_ms != null && (
                    <span style={{ fontSize: '10px', color: '#3A3835', flexShrink: 0 }}>{formatDuration(step.duration_ms)}</span>
                  )}
                </div>
              </div>
              {/* Thinking sub-message under running step */}
              {step.status === 'running' && latestThinking && (
                <p style={{ fontSize: '11px', color: '#8A8882', margin: '4px 0 0 26px', fontStyle: 'italic', lineHeight: 1.4 }}>
                  {latestThinking}
                </p>
              )}
              {/* File activity — shown under ai_generate step */}
              {step.id === 'ai_generate' && step.status === 'running' && (
                <FileActivityFeed active />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Animated file edit feed (like Lovable's "Edited index.css")
const FILE_SEQUENCE = [
  { action: 'Reading', file: 'reference analysis' },
  { action: 'Building', file: 'design system' },
  { action: 'Writing', file: 'index.html' },
  { action: 'Writing', file: 'style.css' },
  { action: 'Editing', file: 'nav section' },
  { action: 'Editing', file: 'hero section' },
  { action: 'Editing', file: 'features section' },
  { action: 'Editing', file: 'footer section' },
  { action: 'Editing', file: 'fonts.css' },
];

function FileActivityFeed({ active }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    setCount(0);
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setCount(i);
      if (i >= FILE_SEQUENCE.length) clearInterval(iv);
    }, 1100);
    return () => clearInterval(iv);
  }, [active]);

  if (!active || count === 0) return null;
  const items = FILE_SEQUENCE.slice(0, count);

  return (
    <div style={{ marginLeft: '26px', marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {items.slice(-4).map((item, i, arr) => {
        const isLatest = i === arr.length - 1;
        return (
          <div key={i} className="flex items-center gap-2" style={{ opacity: isLatest ? 1 : 0.35 }}>
            <span style={{ fontSize: '10px', fontWeight: 500, color: isLatest ? 'var(--orbis-blue)' : 'var(--orbis-green)', letterSpacing: '0.04em', width: '44px', flexShrink: 0 }}>{item.action}</span>
            <span style={{ fontSize: '11px', color: isLatest ? 'var(--orbis-warm)' : '#5A5855', fontFamily: 'var(--orbis-mono)' }}>{item.file}</span>
            {isLatest && (
              <div className="flex gap-0.5">
                {[0, 1, 2].map(j => (
                  <div key={j} style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'var(--orbis-blue)', animation: `ap-dot 1.2s ${j * 0.2}s ease-in-out infinite` }} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// User message bubble
function UserMessage({ text }) {
  return (
    <div className="flex justify-end">
      <div style={{ background: 'rgba(255,255,255,0.07)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: '16px 16px 4px 16px', padding: '10px 14px', maxWidth: '85%' }}>
        <p style={{ fontSize: '13px', fontWeight: 300, color: 'var(--orbis-warm)', margin: 0, lineHeight: 1.55 }}>{text}</p>
      </div>
    </div>
  );
}

// Agent message with optional tool card
function AgentMessage({ msg, buildState }) {
  const elapsedSec = msg.elapsedSec;
  return (
    <div className="flex flex-col gap-0">
      {elapsedSec && (
        <p style={{ fontSize: '11px', color: '#3A3835', margin: '0 0 6px 0' }}>
          Thought for {elapsedSec}s
        </p>
      )}
      <p style={{ fontSize: '13px', fontWeight: 300, color: 'var(--orbis-warm)', lineHeight: 1.65, margin: 0 }}>{msg.text}</p>
      {msg.toolCard && (
        <ToolCard card={msg.toolCard} buildState={msg.isLiveBuild ? buildState : null} />
      )}
    </div>
  );
}

// Suggestion chips — one-click quick replies below agent messages
function SuggestionChips({ suggestions, onChipClick, disabled }) {
  const [clicked, setClicked] = useState(false);
  if (!suggestions?.length || clicked || disabled) return null;

  return (
    <div className="flex flex-wrap gap-2" style={{ marginTop: '10px' }}>
      {suggestions.map((chip, i) => (
        <button
          key={i}
          onClick={() => { setClicked(true); onChipClick?.(chip); }}
          style={{
            fontSize: '12px',
            padding: '6px 14px',
            borderRadius: '100px',
            background: 'rgba(184,204,223,0.08)',
            border: '0.5px solid rgba(184,204,223,0.15)',
            color: 'var(--orbis-blue)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'var(--orbis-font)',
            fontWeight: 400,
          }}
          onMouseEnter={e => { e.target.style.background = 'rgba(184,204,223,0.15)'; e.target.style.borderColor = 'rgba(184,204,223,0.3)'; }}
          onMouseLeave={e => { e.target.style.background = 'rgba(184,204,223,0.08)'; e.target.style.borderColor = 'rgba(184,204,223,0.15)'; }}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}

// Typing indicator for when agent is "thinking"
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5" style={{ padding: '8px 0' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#5A5855', animation: `ap-dot 1.2s ${i * 0.2}s ease-in-out infinite` }} />
      ))}
    </div>
  );
}

const STAGE_PLACEHOLDERS = {
  reference: 'Ask me anything about this site...',
  brief: 'Tell me about your brand...',
  build: 'Ask me anything while building...',
  review: 'Ask me to change anything...',
};

const INTENT_LABELS = { match_visual_style: 'Style Match', structure_inspiration: 'Structure', redesign_reference: 'Redesign', create_my_version: 'My Version' };
const STATUS_COLORS = { complete: '#34D399', partial: '#FBBF24', blocked: '#F87171' };

export default function CloneAgentPanel({
  messages = [],
  buildState = null,       // live SSE state from CloneModePage (build stage only)
  currentStage = 'reference',
  isProcessing = false,
  onSendMessage,
  onSuggestionClick,       // (chip: {label, action, payload}) => void
  cloneIntent,
  referenceFidelity,
  analysisStatus,
}) {
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, buildState?.steps?.length, buildState?.thinkingMessages?.length]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isProcessing) return;
    setInput('');
    onSendMessage?.(text);
  }, [input, isProcessing, onSendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      style={{
        width: '360px',
        flexShrink: 0,
        background: 'var(--orbis-bg)',
        borderRight: '0.5px solid rgba(255,255,255,0.07)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2.5 flex-shrink-0 px-4"
        style={{ height: '48px', borderBottom: '0.5px solid rgba(255,255,255,0.07)', background: 'rgba(14,14,14,0.8)' }}
      >
        <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'var(--orbis-blue)' }}>
          <OrbisMarkSVG size={12} />
        </div>
        <span style={{ fontSize: '13px', fontWeight: 300, color: '#8A8882' }}>{currentStage === 'review' ? 'Orbis Agent' : 'Clone Guide'}</span>
        {(buildState?.isBuilding && !buildState?.isComplete) && (
          <div className="flex items-center gap-1.5 ml-auto">
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--orbis-blue)', animation: 'ap-pulse 1.5s ease-in-out infinite' }} />
            <span style={{ fontSize: '11px', color: '#5A5855' }}>Building</span>
          </div>
        )}
        {currentStage === 'review' && (
          <span style={{ fontSize: '11px', color: '#5A5855', marginLeft: 'auto', background: 'rgba(143,196,160,0.08)', padding: '3px 8px', borderRadius: '100px', border: '0.5px solid rgba(143,196,160,0.2)' }}>
            Edit mode
          </span>
        )}
      </div>

      {/* Clone strategy chips */}
      {cloneIntent && currentStage === 'build' && (
        <div className="flex items-center gap-1.5 px-4 py-1.5 flex-shrink-0" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.05)', background: 'rgba(14,14,14,0.5)' }}>
          <span style={{ fontSize: '10px', padding: '1px 8px', borderRadius: '100px', background: 'rgba(184,204,223,0.08)', border: '0.5px solid rgba(184,204,223,0.15)', color: 'var(--orbis-blue)' }}>
            {INTENT_LABELS[cloneIntent] || cloneIntent}
          </span>
          <span style={{ fontSize: '10px', padding: '1px 8px', borderRadius: '100px', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)', color: '#8A8882' }}>
            {referenceFidelity ?? 72}%
          </span>
          {analysisStatus && analysisStatus !== 'complete' && (
            <span style={{ fontSize: '10px', padding: '1px 8px', borderRadius: '100px', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)', color: STATUS_COLORS[analysisStatus] || '#8A8882' }}>
              {analysisStatus === 'blocked' ? 'Blocked' : 'Partial'}
            </span>
          )}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto" style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {messages.map((msg, idx) => {
          // Only the LAST agent message with suggestions gets active chips
          const isLastWithSuggestions = msg.role === 'agent' && msg.suggestions?.length > 0
            && !messages.slice(idx + 1).some(m => m.role === 'agent' && m.suggestions?.length > 0);
          return (
            <div key={msg.id}>
              {msg.role === 'user'
                ? <UserMessage text={msg.text} />
                : <>
                    <AgentMessage msg={msg} buildState={msg.isLiveBuild ? buildState : null} />
                    {msg.suggestions?.length > 0 && (
                      <SuggestionChips
                        suggestions={msg.suggestions}
                        onChipClick={onSuggestionClick}
                        disabled={!isLastWithSuggestions}
                      />
                    )}
                  </>
              }
            </div>
          );
        })}
        {isProcessing && <TypingIndicator />}
      </div>

      {/* Chat input — always visible, enabled in review mode */}
      <div
        className="flex-shrink-0"
        style={{ padding: '12px', borderTop: '0.5px solid rgba(255,255,255,0.07)', background: 'rgba(14,14,14,0.9)' }}
      >
        <div
          className="flex items-end gap-2"
          style={{ background: '#1A1A1A', border: '0.5px solid rgba(184,204,223,0.15)', borderRadius: '12px', padding: '10px 12px' }}
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isProcessing}
            placeholder={STAGE_PLACEHOLDERS[currentStage] || 'Ask me anything...'}
            rows={1}
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              outline: 'none',
              resize: 'none',
              fontSize: '13px',
              fontWeight: 300,
              color: 'var(--orbis-warm)',
              fontFamily: 'var(--orbis-font)',
              lineHeight: 1.5,
              cursor: 'text',
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isProcessing}
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '8px',
              background: (input.trim() && !isProcessing) ? 'var(--orbis-blue)' : 'rgba(255,255,255,0.06)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: (input.trim() && !isProcessing) ? 'pointer' : 'default',
              flexShrink: 0,
              transition: 'background 0.2s ease',
            }}
          >
            <ArrowUp className="w-3.5 h-3.5" style={{ color: (input.trim() && !isProcessing) ? 'var(--orbis-bg)' : '#3A3835' }} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes ap-dot {
          0%, 60%, 100% { opacity: 0.2; transform: scale(0.8); }
          30% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes ap-pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
